/** 结果导出：CSV（指标汇总）/ JSON（事件日志）（F-021）。 */
import type { MetricsSummary } from '../core/metrics/collector';
import type { StrategyParams } from '../strategy/strategy-api';
import { strategyLabel } from '../strategy/registry';
import { sceneLabel } from './templates';

/** 单次实验结果行（批量与单跑共用）。 */
export interface ExperimentResult {
  readonly id: string;
  readonly label: string;
  readonly sceneId: string; // 模板 id 或 'custom'
  readonly strategyId: string;
  readonly seed: number;
  readonly summary: MetricsSummary;
  readonly params?: StrategyParams;
  readonly durationMs?: number; // 真实运行耗时
  readonly ok: boolean;
  readonly error?: string;
}

const COLUMNS: { key: keyof ExperimentResult | string; header: string; get: (r: ExperimentResult) => string | number }[] = [
  { key: 'sceneId', header: 'scene', get: (r) => sceneLabel(r.sceneId) },
  { key: 'strategyId', header: 'strategy', get: (r) => strategyLabel(r.strategyId) },
  { key: 'seed', header: 'seed', get: (r) => r.seed },
  { key: 'ok', header: 'ok', get: (r) => (r.ok ? 1 : 0) },
  { key: 'error', header: 'error', get: (r) => r.error ?? '' },
  { key: 'delivered', header: 'delivered', get: (r) => r.summary.deliveredCount },
  { key: 'avgWaitMs', header: 'avg_wait_ms', get: (r) => Math.round(r.summary.avgWaitMs) },
  { key: 'maxWaitMs', header: 'max_wait_ms', get: (r) => r.summary.maxWaitMs },
  { key: 'avgRideMs', header: 'avg_ride_ms', get: (r) => Math.round(r.summary.avgRideMs) },
  { key: 'throughput', header: 'throughput_per_hour', get: (r) => r.summary.throughputPerHour.toFixed(2) },
  { key: 'utilization', header: 'car_utilization', get: (r) => r.summary.carUtilization.toFixed(4) },
  { key: 'energyKwh', header: 'energy_kwh', get: (r) => r.summary.energyKwh.toFixed(4) },
  { key: 'durationMs', header: 'wall_ms', get: (r) => r.durationMs ?? '' },
];

function csvCell(v: string | number): string {
  const s = String(v);
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

/** 多行结果 → CSV（含表头，Excel/pandas 可直接打开，AC-F021-01）。 */
export function resultsToCsv(results: readonly ExperimentResult[]): string {
  const header = COLUMNS.map((c) => c.header).join(',');
  const rows = results.map((r) => COLUMNS.map((c) => csvCell(c.get(r))).join(','));
  return [header, ...rows].join('\n');
}
